'use client';

import { useEffect } from 'react';

const CHUNK_RELOAD_GUARD_KEY = '__next_chunk_load_retry__';
const CHUNK_RELOAD_GUARD_TTL_MS = 30_000;

function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  if (reason instanceof Error) {
    const text = `${reason.name} ${reason.message}`.toLowerCase();
    return text.includes('chunkloaderror') || text.includes('loading chunk');
  }
  if (typeof reason === 'string') {
    const text = reason.toLowerCase();
    return text.includes('chunkloaderror') || text.includes('loading chunk');
  }
  if (typeof reason === 'object') {
    const maybe = reason as { name?: unknown; message?: unknown };
    const name = typeof maybe.name === 'string' ? maybe.name : '';
    const message = typeof maybe.message === 'string' ? maybe.message : '';
    const text = `${name} ${message}`.toLowerCase();
    return text.includes('chunkloaderror') || text.includes('loading chunk');
  }
  return false;
}

function canReloadForCurrentRoute(): boolean {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (!raw) {
      return true;
    }
    const parsed = JSON.parse(raw) as { route?: unknown; ts?: unknown };
    const route =
      typeof parsed.route === 'string' ? parsed.route : window.location.href;
    const ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
    const sameRoute = route === window.location.href;
    const inWindow = Date.now() - ts < CHUNK_RELOAD_GUARD_TTL_MS;
    return !(sameRoute && inWindow);
  } catch {
    return true;
  }
}

function markReloadForCurrentRoute() {
  try {
    window.sessionStorage.setItem(
      CHUNK_RELOAD_GUARD_KEY,
      JSON.stringify({
        route: window.location.href,
        ts: Date.now(),
      })
    );
  } catch {
    // Ignore session storage failures.
  }
}

function clearStaleReloadMark() {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { ts?: unknown };
    const ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
    if (Date.now() - ts > CHUNK_RELOAD_GUARD_TTL_MS) {
      window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    }
  } catch {
    // Ignore storage read failures.
  }
}

function reloadOnceOnChunkFailure(reason: unknown) {
  if (!isChunkLoadError(reason)) return;
  if (!canReloadForCurrentRoute()) return;
  markReloadForCurrentRoute();
  window.location.reload();
}

export function ChunkLoadRecovery() {
  useEffect(() => {
    clearStaleReloadMark();

    const onError = (event: ErrorEvent) => {
      reloadOnceOnChunkFailure(event.error ?? event.message);
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      reloadOnceOnChunkFailure(event.reason);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return null;
}
